/**
 * ExpertWalletDashboard.jsx
 * Dashboard Expert avec système de crédits ESS
 * 
 * Les crédits sont débités automatiquement lors des actions productives
 * La consultation simple reste gratuite
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent } from './ui/dialog';
import { Wallet, FileText, Loader2, BarChart3, History, RefreshCw, HelpCircle, BookOpen, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import CreditsPricingGuide from './CreditsPricingGuide';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Configuration des actions Expert (pour l'historique)
const EXPERT_ACTIONS = {
  EXPERT_REPORT_SIMPLE: { label: "Rapport d'analyse simple", credits: 6 },
  EXPERT_REPORT_TECHNICAL: { label: "Rapport technique / juridique", credits: 10 },
  EXPERT_REPORT_LONG: { label: "Rapport long avec annexes", credits: 15 },
  EXPERT_REPORT_AMENDMENT: { label: "Avenant / mise à jour de rapport", credits: 5 },
  EXPERT_LETTER_SIMPLE: { label: "Courrier simple", credits: 3 },
  EXPERT_LETTER_ARGUMENTED: { label: "Courrier argumenté / juridique", credits: 5 },
  EXPERT_LETTER_PROBANT: { label: "Courrier probant (PDF signé)", credits: 7 },
  EXPERT_MESSAGE_SHORT: { label: "Message court", credits: 1 },
  EXPERT_MESSAGE_STRUCTURED: { label: "Message structuré / PJ", credits: 2 },
  EXPERT_MESSAGE_ARCHIVED: { label: "Message officiel archivé GED", credits: 3 },
};

const ExpertWalletDashboard = () => {
  const { getAuthHeader } = useAuth();
  const [wallet, setWallet] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showPricingGuide, setShowPricingGuide] = useState(false);

  const fetchWalletData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = getAuthHeader();
      const [walletRes, statsRes] = await Promise.all([
        fetch(`${API}/api/wallets/me`, { headers }).then(r => r.json()),
        fetch(`${API}/api/wallets/expert/stats`, { headers }).then(r => r.json()).catch(() => ({ success: false }))
      ]);
      
      setWallet(walletRes);
      if (statsRes.success) {
        setStats(statsRes);
      }
    } catch {
      toast.error('Erreur lors du chargement du wallet');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeader]);

  useEffect(() => {
    fetchWalletData();
  }, [fetchWalletData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec solde wallet */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <FileText className="h-7 w-7 text-emerald-600" />
            🧠 Espace Expert
          </h2>
          <p className="text-slate-600">Production documentaire avec crédits ESS</p>
        </div>
        <div className="flex items-center gap-4">
          <Button onClick={fetchWalletData} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Actualiser
          </Button>
          <Card className="bg-gradient-to-r from-emerald-500 to-teal-600 text-white">
            <CardContent className="py-3 px-5">
              <div className="flex items-center gap-3">
                <Wallet className="h-6 w-6" />
                <div>
                  <p className="text-xs opacity-80">Solde wallet</p>
                  <p className="text-xl font-bold">{wallet?.credit_balance?.toFixed(0) || 0} crédits</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Règle d'or ESS */}
      <Card className="border-l-4 border-l-amber-500 bg-amber-50">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-semibold text-amber-800">Principe Expert ESS (règle d&apos;or)</p>
              <ul className="text-sm text-amber-700 mt-1 space-y-1">
                <li>👉 Toute production intellectuelle ou action probante consomme des crédits.</li>
                <li>👉 La consultation simple reste gratuite.</li>
                <li>👉 La production, la formalisation ou l&apos;envoi consomme des crédits.</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiques Expert */}
      {stats?.expert_stats && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-emerald-600" />
              Statistiques Expert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-slate-50 rounded-xl p-4">
                <p className="text-sm text-slate-600">Actions ce mois</p>
                <p className="text-2xl font-bold text-slate-800">{stats.expert_stats.total_actions || 0}</p>
              </div>
              <div className="bg-emerald-50 rounded-xl p-4">
                <p className="text-sm text-emerald-600">Crédits utilisés</p>
                <p className="text-2xl font-bold text-emerald-700">{stats.expert_stats.total_credits_consumed || 0}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-4">
                <p className="text-sm text-blue-600">Solde disponible</p>
                <p className="text-2xl font-bold text-blue-700">{wallet?.credit_balance?.toFixed(0) || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Historique récent */}
      {stats?.recent_transactions && stats.recent_transactions.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-slate-600" />
              Historique récent
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {stats.recent_transactions.slice(0, 5).map((tx, index) => {
                const actionConfig = EXPERT_ACTIONS[tx.action_type?.toUpperCase()] || {};
                return (
                  <div key={index} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        tx.credits_delta < 0 ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'
                      }`}>
                        {tx.credits_delta < 0 ? '-' : '+'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-800">{actionConfig.label || tx.description}</p>
                        <p className="text-xs text-slate-500">
                          {new Date(tx.created_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'short', 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </p>
                      </div>
                    </div>
                    <Badge variant={tx.credits_delta < 0 ? 'destructive' : 'success'}>
                      {tx.credits_delta < 0 ? '' : '+'}{Math.abs(tx.credits_delta)} crédits
                    </Badge>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Mention contractuelle ESS */}
      <Card className="bg-slate-50">
        <CardContent className="py-4">
          <p className="text-xs text-slate-500 italic">
            Les experts utilisent des crédits pour produire des rapports, courriers et communications formalisées.
            Les crédits rémunèrent un service intellectuel et documentaire.
            Ils ne constituent ni honoraires horaires, ni cotisation d&apos;assurance.
          </p>
        </CardContent>
      </Card>

      {/* Bouton vers le guide des crédits */}
      <Button 
        variant="outline" 
        onClick={() => setShowPricingGuide(true)}
        className="w-full flex items-center justify-center gap-2 text-blue-600 border-blue-200 hover:bg-blue-50"
      >
        <HelpCircle className="h-4 w-4" />
        Comment fonctionnent les crédits ?
        <BookOpen className="h-4 w-4" />
      </Button>

      {/* Modal du guide des crédits */}
      <Dialog open={showPricingGuide} onOpenChange={setShowPricingGuide}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto p-0">
          <CreditsPricingGuide userRole="expert" onClose={() => setShowPricingGuide(false)} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ExpertWalletDashboard;
